import { ClassNames } from '@emotion/react'
import styled from '@emotion/styled'
import { Transition } from '@headlessui/react'
import { ReactNode, useEffect, useRef, useState } from 'react'

import { theme } from '../constants'

const Parent = styled.div`
  position: relative;
  transition: height ${theme.animation.transition};

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

export type ContentChangeTransitionProps = {
  children: ReactNode
  /**
   * When this value changes, the transition is triggered
   */
  transitionTrigger: unknown
}

let idCounter = 0

/**
 * Transitions between sets of content smoothly by fading in/out the content
 * and animating the height change.
 */
export const ContentChangeTransition = ({
  children,
  transitionTrigger,
}: ContentChangeTransitionProps) => {
  const initialRender = useRef(true)
  const parentRef = useRef<HTMLDivElement>(null)
  const [height, setHeight] = useState('auto')
  const [childrenItem, setChildrenItem] = useState<
    { id: number; children: ReactNode }[]
  >([
    {
      id: idCounter,
      children,
    },
  ])

  const parentElement = parentRef.current

  useEffect(() => {
    // Already contains the first childrenItem
    if (initialRender.current) {
      initialRender.current = false
      return
    }

    setChildrenItem((currentChildren) => [
      ...currentChildren,
      {
        id: ++idCounter,
        children,
      },
    ])

    setTimeout(() => {
      setChildrenItem((currentChildren) => currentChildren.slice(1))
    }, theme.animation.duration * 1000)
  }, [transitionTrigger])

  useEffect(() => {
    if (childrenItem.length === 1 && parentElement) {
      setHeight('auto')
    }
  }, [childrenItem.length])

  /*
    Not loving needing to get height on every render. But couldn't get a reliable value from
    Transition's beforeEnter or the other hooks
  */
  const fromHeight = parentElement
    ? getComputedStyle(parentElement).height
    : 'auto'

  return (
    <Parent
      ref={parentRef}
      style={{
        overflow: childrenItem.length > 1 ? 'hidden' : '',
        height,
      }}
    >
      <ClassNames>
        {({ css }) =>
          childrenItem.map(({ children, id }, index, arr) => (
            <Transition
              key={id}
              ref={(node) => {
                if (!node || !parentElement) {
                  return
                }

                const toHeight = getComputedStyle(node).height

                setHeight(fromHeight)

                requestAnimationFrame(() => {
                  setHeight(toHeight)
                })
              }}
              className={css`
                /* If a child e.g. has margin-top, it will be included in the parent's height calculation */
                display: flow-root;
                top: 0;
                right: 0;
                left: 0;
              `}
              appear={index !== 0} // So that it won't fade in on first render
              show={index === arr.length - 1} // fade out everything that isn't the last element
              leave={css`
                transition: opacity ${theme.animation.transition};
              `}
              leaveFrom={css`
                opacity: 1;
              `}
              leaveTo={css`
                position: absolute;
                opacity: 0;
              `}
              enter={css`
                transition: opacity ${theme.animation.transition};
              `}
              enterFrom={css`
                opacity: 0;
              `}
              enterTo={css`
                opacity: 1;
              `}
            >
              {children}
            </Transition>
          ))
        }
      </ClassNames>
    </Parent>
  )
}
